<script setup lang="ts">
import Button from '@/components/buttons/Button.vue';
import DatabaseSettings from '@/components/DatabaseSettings/DatabaseSettings.vue';
import VeeSubmitButton from '@/components/forms/SubmitButton/VeeSubmitButton.vue';
import { useAuthenticationStore } from '@/stores/authenticationStore';
import { useDatabaseSettings } from '@/utils/useDatabaseSettings.ts';
import InstallationStep from '@/views/InstallationView/components/InstallationStep.vue';
import type {
  InstallationStepDatabaseSettingsEmits,
  InstallationStepDatabaseSettingsProps,
} from '@/views/InstallationView/types.ts';
import { storeToRefs } from 'pinia';
import { computed, ref, watch } from 'vue';

const props = defineProps<InstallationStepDatabaseSettingsProps>();
const emit = defineEmits<InstallationStepDatabaseSettingsEmits>();

const authenticationStore = useAuthenticationStore();
const { session } = storeToRefs(authenticationStore);

// Create installation config from props
const installationConfigFromProps = computed(() => ({
  defaultConnectionString: props.defaultConnectionString || '',
  blobStoringConnectionString: props.blobStoringConnectionString || '',
  integratedDefaultConnectionString: props.integratedDefaultConnectionString || '',
  integratedBlobConnectionString: props.integratedBlobConnectionString || '',
  isCustomDatabaseUsed: props.isCustomDatabaseUsed || false,
  csoPortalUrl: '', // Not needed for database settings
  customerProvidedOptionalSiteName: null,
  emailSettings: null,
  completedStep: undefined,
  $type: undefined,
}));

// Use the unified composable with props-based config
const {
  // Form
  handleSubmit,
  meta,

  // State
  isCustomDatabaseUsed,
  isActive,
  isSubmitting,
  isDefaultPasswordVisible,
  isBlobStoringPasswordVisible,
  isRebooting,
  showSuccessMessage,
  showErrorMessage,
  defaultConnectionString,
  blobStoringConnectionString,

  // Methods
  handleDatabaseTypeChange,
  submitDatabaseSettings,
  updateInstallationConfig,
  toggleDefaultPasswordVisibility,
  toggleBlobStoringPasswordVisibility,
  isWaitingForReboot,
} = useDatabaseSettings({
  autoLoad: false, // Don't autoload for installation wizard
  initialConfig: installationConfigFromProps.value,
});

const databaseType = ref<'integrated' | 'custom'>(
  isCustomDatabaseUsed.value ? 'custom' : 'integrated',
);
const initialDatabaseType = databaseType.value;

function handleDatabaseTypeChangeAndResetValidation(dbType: 'integrated' | 'custom') {
  if (dbType !== initialDatabaseType) {
    emit('change', true);
  } else {
    emit('change', false);
  }
  handleDatabaseTypeChange(dbType);
}

function handleSuccess() {
  emit('completed', { shouldUpdate: true });
}

function handleNext() {
  handleSuccess();
}

const submit = handleSubmit(async (updateConnectionStringsRequestDto) => {
  await submitDatabaseSettings(updateConnectionStringsRequestDto, handleSuccess);
});

// Watch for changes in props to update installation config
watch(
  installationConfigFromProps,
  (config) => {
    updateInstallationConfig(config);
  },
  { immediate: true },
);
</script>

<template>
  <form @submit="submit">
    <InstallationStep>
      <template #content>
        <DatabaseSettings
          v-model:databaseType="databaseType"
          :isFirstTimeInstallation="session?.isFirstTimeInstallation"
          :isCustomDatabaseUsed="isCustomDatabaseUsed"
          :defaultConnectionString="defaultConnectionString"
          :blobStoringConnectionString="blobStoringConnectionString"
          :isProcessing="isActive"
          :isRebooting="isRebooting"
          :isWaitingForReboot="isWaitingForReboot"
          :showSuccessMessage="showSuccessMessage"
          :showErrorMessage="showErrorMessage"
          :showProgress="isActive"
          :isDefaultPasswordVisible="isDefaultPasswordVisible"
          :isBlobStoringPasswordVisible="isBlobStoringPasswordVisible"
          @update:databaseType="handleDatabaseTypeChangeAndResetValidation"
          @update:passwordVisibility="
            (field, visible) => {
              if (field === 'default') toggleDefaultPasswordVisibility();
              if (field === 'blob') toggleBlobStoringPasswordVisibility();
            }
          "
        />
      </template>
      <template #footer>
        <VeeSubmitButton class="mt-8" :loading="isSubmitting" :text="$t('SaveAndConnect')" />
        <Button
          variant="secondary"
          class="ml-2 mt-8"
          :disabled="isActive || !meta.valid"
          :label="$t('Next')"
          @click="handleNext"
        />
      </template>
    </InstallationStep>
  </form>
</template>
